import { Heart, Pencil, Pin } from 'lucide-react'

import { ColorSimulationImageCreatedByType } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'

import { cn } from '@modules/lib/lib'
import type { PinnedHistoryItem } from '../hooks/useHistoryFilter'
import type { ThumbnailGroup, ThumbnailItem } from '../hooks/useThumbnailGroups'
import { formatHistoryDate, resolveHistoryTitle } from '../utils/historyDisplay'

interface CustomerPinnedStripProps {
  items: PinnedHistoryItem[]
  companyName?: string
  onSelect: (item: ThumbnailItem) => void
}

/**
 * 施主画面の履歴エリア先頭に出す横並びのピン留めカード（決定事項2。モックのCustomerLanding）。
 * 施主はピンを見るだけで付け外しはできない。1件以上あるときだけ描画する。
 */
export const CustomerPinnedStrip: React.FC<CustomerPinnedStripProps> = ({
  items,
  companyName,
  onSelect,
}) => {
  if (items.length === 0) return null
  return (
    <section className="flex flex-col gap-2.5" data-testid="customer-pinned-strip">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1 text-sm font-bold text-foreground">
          <Pin aria-hidden className="h-3.5 w-3.5 fill-orange-600 text-orange-600" />
          ピン留め{' '}
          <span className="text-xs font-normal text-muted-foreground">{items.length}件</span>
        </h3>
        {companyName ? (
          <span className="text-[11px] text-muted-foreground">{companyName}がピン留めした履歴</span>
        ) : null}
      </div>
      <div className="flex gap-2.5 overflow-x-auto pb-1">
        {items.map(({ item }) => {
          const title = resolveHistoryTitle(item)
          return (
            <button
              aria-label={title.text}
              aria-pressed={item.isActive}
              className="flex w-[120px] shrink-0 flex-col gap-1 text-left"
              key={item.key}
              onClick={() => onSelect(item)}
              type="button"
            >
              <span
                className={cn(
                  'relative block h-[90px] w-[120px] overflow-hidden rounded-lg bg-muted',
                  item.isActive ? 'border-2 border-foreground' : 'border border-input',
                )}
              >
                <img alt="" className="h-full w-full object-cover" src={item.url} />
                <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-600 text-white">
                  <Pin aria-hidden className="h-3 w-3 fill-current" />
                </span>
              </span>
              <span
                className={cn(
                  'truncate text-[11px]',
                  title.isDefault ? 'text-muted-foreground' : 'font-semibold text-foreground',
                )}
              >
                {title.text}
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

interface CustomerHistoryListProps {
  groups: ThumbnailGroup[]
  /** 絞り込みチップなど、見出しの右に置くもの */
  headerAccessory?: React.ReactNode
  /** 絞り込み中の案内（例: お気に入りにした履歴だけを表示しています） */
  filterNote?: string
  onSelect: (item: ThumbnailItem) => void
  onToggleFavorite?: (imageId: number, favorited: boolean) => Promise<void> | void
  /** タイトル編集のシートを開く */
  onEditTitle?: (item: ThumbnailItem) => void
}

/**
 * 施主画面の履歴一覧（行の形。モックのCustomerLanding / CustomerBrowse）。
 * カットごとに見出し「履歴 外観カットN」を出し、行にサムネイル・タイトル・作成者・年月日・
 * ピン留め表示・お気に入り・タイトル編集ボタン（施主自身が実行した履歴だけ）を置く。
 * 施主画面なので日付に時刻は出さない（企業が遅い時間に作業した時刻を見せない。藤井の決定）。
 */
export const CustomerHistoryList: React.FC<CustomerHistoryListProps> = ({
  groups,
  headerAccessory,
  filterNote,
  onSelect,
  onToggleFavorite,
  onEditTitle,
}) => (
  <section className="flex flex-col gap-2" data-testid="customer-history-list">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-bold text-foreground">履歴</h3>
      {headerAccessory}
    </div>
    {filterNote ? <p className="text-[11px] text-muted-foreground">{filterNote}</p> : null}
    {groups.map(group => (
      <div className="flex flex-col" key={group.key}>
        <p className="py-1 text-xs text-muted-foreground">{group.title}</p>
        {group.items.length === 0 ? (
          <p className="py-2 text-xs text-muted-foreground">該当する履歴はありません</p>
        ) : null}
        {group.items.map(item => {
          const title =
            item.type === 'original'
              ? { text: '現況写真', isDefault: false }
              : resolveHistoryTitle(item)
          const meta = [item.createdBy, formatHistoryDate(item.createdAt) || undefined]
            .filter(Boolean)
            .join('・')
          return (
            <div
              className="flex items-center border-t border-input"
              data-testid="customer-history-row"
              key={item.key}
            >
              {/* 行のうち、お気に入り・タイトル編集のボタン以外の全面を1つの選択ボタンにする（上下の余白とボタンとの隙間も含む） */}
              <button
                aria-label={item.type === 'original' ? (item.createdBy ?? title.text) : title.text}
                aria-pressed={item.isActive}
                className="flex min-w-0 flex-1 items-center gap-2.5 self-stretch py-2 pr-2.5 text-left"
                onClick={() => onSelect(item)}
                type="button"
              >
                <span
                  className={cn(
                    'block h-[66px] w-[88px] shrink-0 overflow-hidden rounded-lg bg-muted',
                    item.isActive ? 'border-2 border-foreground' : 'border border-input',
                  )}
                >
                  <img alt="" className="h-full w-full object-cover" src={item.url} />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-[13px]',
                      title.isDefault ? 'font-normal text-muted-foreground' : 'font-semibold',
                    )}
                  >
                    {title.text}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {title.isDefault && item.type === 'processed' ? 'タイトル未設定' : meta}
                    {item.pinnedAt ? <span className="text-orange-800">・ピン留め</span> : null}
                  </span>
                  {item.isActive ? (
                    <span className="mt-1 inline-block rounded bg-foreground px-1.5 text-[10px] text-background">
                      表示中
                    </span>
                  ) : null}
                </span>
              </button>
              {item.type === 'processed' ? (
                <>
                  <button
                    aria-label={item.favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
                    aria-pressed={item.favorited}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
                    disabled={!onToggleFavorite || !item.imageId}
                    onClick={() => onToggleFavorite?.(item.imageId!, !item.favorited)}
                    type="button"
                  >
                    <Heart
                      className={cn(
                        'h-5 w-5',
                        item.favorited ? 'fill-emerald-500 text-emerald-500' : 'text-slate-600',
                      )}
                    />
                  </button>
                  {/* タイトルを変えられるのは施主自身が実行した履歴だけ（企業の提案のタイトルは書き換えられない） */}
                  {onEditTitle &&
                  item.createdByType === ColorSimulationImageCreatedByType.CUSTOMER ? (
                    <button
                      aria-label={title.isDefault ? 'タイトルを付ける' : 'タイトルを編集'}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
                      onClick={() => onEditTitle(item)}
                      type="button"
                    >
                      <Pencil className="h-[18px] w-[18px] text-muted-foreground" />
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          )
        })}
      </div>
    ))}
  </section>
)
